import { useState, useEffect, useRef } from 'react';
import { getCompletedExtractionJobs, executeRulesInTestMode } from '../services/api/ruleTestingApi';
import { getAugmentationRules } from '../services/api/augmentationRuleApi';
import { getCustomersForDropdown } from '../services/api/customerApi';
import { getRulesetTypesByCustomer } from '../services/api/rulesetApi';
import { validateJsonInput } from '../utils/jsonValidation';

export const useRuleTesting = () => {
  const [customers, setCustomers] = useState([]);
  const [rulesetTypes, setRulesetTypes] = useState([]);
  const [extractionJobs, setExtractionJobs] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedRulesetTypeId, setSelectedRulesetTypeId] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const [inputMode, setInputMode] = useState('extraction-job');
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const hasFetchedCustomers = useRef(false);

  // Fetch customers on mount
  useEffect(() => {
    if (!hasFetchedCustomers.current) {
      hasFetchedCustomers.current = true;
      fetchCustomers();
    }
  }, []);

  // Fetch related data when customer or ruleset type changes
  useEffect(() => {
    if (selectedCustomerId) {
      fetchRulesetTypes();
      fetchExtractionJobs();
      fetchRules();
    } else {
      setRulesetTypes([]);
      setExtractionJobs([]);
      setRules([]);
    }
  }, [selectedCustomerId, selectedRulesetTypeId]);


  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomersForDropdown();
      const customerList = response.data.items || response.data || [];
      setCustomers(customerList);
      
      // Auto-select first customer if available
      if (customerList.length > 0) {
        setSelectedCustomerId(customerList[0].id);
      }
    } catch (err) {
      setError('Failed to fetch customers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRulesetTypes = async () => {
    if (!selectedCustomerId) return;
    
    try {
      const response = await getRulesetTypesByCustomer(selectedCustomerId);
      setRulesetTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch ruleset types:', err);
      setRulesetTypes([]);
    }
  };

  const fetchExtractionJobs = async () => {
    if (!selectedCustomerId) return;

    try {
      setLoading(true);
      const response = await getCompletedExtractionJobs(
        selectedCustomerId,
        selectedRulesetTypeId || null
      );
      setExtractionJobs(response.data || []);
    } catch (err) {
      setError('Failed to fetch extraction jobs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    if (!selectedCustomerId) return;

    try {
      setLoading(true);
      const response = await getAugmentationRules(
        selectedCustomerId,
        selectedRulesetTypeId || null
      );
      // Sort rules by execution order and filter to active rules
      const sortedRules = (response.data || [])
        .filter(rule => rule.isActive)
        .sort((a, b) => a.executionOrder - b.executionOrder);
      setRules(sortedRules);
    } catch (err) {
      setError('Failed to fetch augmentation rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const executeTest = async (extractionJobId, ruleIds) => {
    if (ruleIds.length === 0) {
      setError('Please select at least one rule to test');
      throw new Error('No rules selected');
    }

    // Shared execution helper to avoid duplication
    const runExecution = async (jobId, inputData) => {
      try {
        setTesting(true);
        setError(null);
        setSuccess(null);
        setTestResults(null);

        const response = await executeRulesInTestMode(jobId, ruleIds, inputData);

        setTestResults(response.data);
        setSuccess(
          `Test completed successfully! Executed ${response.data.ruleResults.length} rules in ${response.data.totalExecutionTimeMs.toFixed(2)}ms`
        );

        setTimeout(() => setSuccess(null), 5000);
        return response.data;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to execute rule test');
        console.error(err);
        throw err;
      } finally {
        setTesting(false);
      }
    };

    // Paste-JSON mode: validate JSON and pass inputData to API
    if (inputMode === 'paste-json') {
      const validation = validateJsonInput(jsonInput);
      if (!validation.valid) {
        setJsonError(validation.error);
        throw new Error(validation.error);
      }
      setJsonError(null);
      return runExecution(null, validation.data);
    }

    // Extraction-job mode: existing behavior
    if (!extractionJobId) {
      setError('Please select an extraction job');
      throw new Error('No extraction job selected');
    }

    return runExecution(extractionJobId, undefined);
  };

  const clearTestResults = () => {
    setTestResults(null);
  };

  return {
    customers,
    rulesetTypes,
    extractionJobs,
    rules,
    loading,
    error,
    success,
    selectedCustomerId,
    selectedRulesetTypeId,
    testResults,
    testing,
    inputMode,
    setInputMode,
    jsonInput,
    setJsonInput,
    jsonError,
    setJsonError,
    setSelectedCustomerId,
    setSelectedRulesetTypeId,
    executeTest,
    clearTestResults,
  };
};
